import {
  COMMENTS_LOAD_ERROR,
  COMMENT_SEND_ERROR,
  COMMENT_REPLY_SEND_ERROR,
  COMMENT_TEXT_MIN_LENGTH_ERROR,
} from '@/pages/journalArticle/lib/journalArticleMessages';
import { nanoid } from 'nanoid';

import { COMMENTS_PER_PAGE } from '@/pages/journalArticle/lib/useJournalArticleConstants';
import {
  mapArticleComment,
  toArticleCommentUser,
} from '@/pages/journalArticle/lib/mapArticleComment';

import { useUserStore } from '@/entities/user/store';
import type {
  ArticleCommentView,
  ReplyPayload,
} from '@/pages/journalArticle/model/types/articleComments';
import { useErrorHandler } from '@/shared/lib/errors';

const useArticleComments = (
  articleId: ComputedRef<number | undefined>,
  isLoadRequested: ComputedRef<boolean>,
) => {
  const { handleError } = useErrorHandler();
  const { $journalService } = useNuxtApp();
  const userStore = useUserStore();
  const { user } = storeToRefs(userStore);

  const commentsList: Ref<ArticleCommentView[]> = ref([]);
  const totalItems: Ref<number> = ref(0);
  const currentPage: Ref<number> = ref(1);
  const isShowLoadMore: Ref<boolean> = ref(false);
  const commentError: Ref<string> = ref('');

  const getComments = async (): Promise<void> => {
    if (!articleId.value) return;

    try {
      const response = await $journalService.getComments({
        per_page: COMMENTS_PER_PAGE,
        page: currentPage.value,
        journal_id: articleId.value,
        'sort-created_at': 'desc',
      });
      const data = response.data ?? [];

      commentsList.value = [...commentsList.value, ...data.map(mapArticleComment)];
      totalItems.value = response.meta?.total ?? commentsList.value.length;
      isShowLoadMore.value = commentsList.value.length < totalItems.value;
    } catch (error) {
      handleError(error, { context: { action: COMMENTS_LOAD_ERROR } });
    }
  };

  const loadMore = (): void => {
    currentPage.value += 1;
    getComments();
  };

  const resetCommentError = (): void => {
    commentError.value = '';
  };

  const addComment = async (text: string): Promise<boolean> => {
    if (!articleId.value || !user.value) return false;

    commentError.value = '';
    const creator = toArticleCommentUser(user.value);

    try {
      const response = await $journalService.createComment({
        journal_id: articleId.value,
        user_id: user.value.id,
        text: text.trim(),
      });

      commentsList.value.unshift({
        id: response.data?.id ?? nanoid(),
        fullText: text,
        createdAt: new Date().toISOString(),
        creator,
        review: '',
        target: null,
      });
      totalItems.value += 1;
      return true;
    } catch (error) {
      const { fields } = handleError(error, { context: { action: COMMENT_SEND_ERROR } });
      if (fields.text) commentError.value = COMMENT_TEXT_MIN_LENGTH_ERROR;
      return false;
    }
  };

  const sendReply = async ({ commentId, text }: ReplyPayload): Promise<void> => {
    if (!articleId.value || !user.value) return;

    const comment = commentsList.value.find((item) => item.id === commentId);
    if (!comment) return;

    const previousReview = comment.review;
    const previousTarget = comment.target;

    comment.review = text;
    comment.target = toArticleCommentUser(user.value);

    try {
      const requestData = {
        parent_id: commentId,
        journal_id: articleId.value,
        user_id: user.value.id,
        text,
      };

      if (comment.replyId) {
        await $journalService.editComment(comment.replyId, requestData);
        return;
      }

      const response = await $journalService.createComment(requestData);
      comment.replyId = response.data?.id;
    } catch (error) {
      comment.review = previousReview;
      comment.target = previousTarget;
      handleError(error, {
        context: { action: COMMENT_REPLY_SEND_ERROR },
      });
    }
  };

  watch(
    isLoadRequested,
    (isRequested) => {
      if (isRequested) getComments();
    },
    { immediate: true },
  );

  return {
    commentsList,
    totalItems,
    isShowLoadMore,
    commentError,
    loadMore,
    addComment,
    resetCommentError,
    sendReply,
  };
};

export default useArticleComments;
